/**
 * GraphQL Server Setup
 * 集成 GraphQL 到 Express API 服务器
 */

import { ApolloServer } from '@apollo/server';
import { expressMiddleware } from '@apollo/server/express4';
import { ApolloServerPluginDrainHttpServer } from '@apollo/server/plugin/drainHttpServer';
import { makeExecutableSchema } from '@graphql-tools/schema';
import { WebSocketServer } from 'ws';
import { useServer } from 'graphql-ws/lib/use/ws';
import { readFileSync } from 'fs';
import { join } from 'path';
import { resolvers } from './resolvers.js';
import { logger } from '../../utils/logger.js';
import { createServer } from 'http';
import express from 'express';

// 读取 GraphQL Schema
const typeDefs = readFileSync(
  new URL('./schema.graphql', import.meta.url).pathname,
  'utf-8'
);

// 创建可执行 Schema
const schema = makeExecutableSchema({
  typeDefs,
  resolvers,
});

// 创建 GraphQL 服务器
export async function createGraphQLServer(app: express.Application) {
  // 创建 HTTP 服务器
  const httpServer = createServer(app);

  // 创建 WebSocket 服务器用于订阅
  const wsServer = new WebSocketServer({
    server: httpServer,
    path: '/graphql',
  });

  // 设置 WebSocket 服务器
  const serverCleanup = useServer(
    {
      schema,
      context: async (ctx, msg, args) => {
        // 可以在这里添加认证逻辑
        return {
          // 添加上下文数据
        };
      },
      onConnect: async (ctx) => {
        logger.info('GraphQL WebSocket client connected');
      },
      onDisconnect: async (ctx, code, reason) => {
        logger.info('GraphQL WebSocket client disconnected');
      },
    },
    wsServer
  );

  // 创建 Apollo Server
  const server = new ApolloServer({
    schema,
    plugins: [
      // 优雅关闭
      ApolloServerPluginDrainHttpServer({ httpServer }),
      {
        async serverWillStart() {
          return {
            async drainServer() {
              await serverCleanup.dispose();
            },
          };
        },
      },
    ],
  });

  // 启动 Apollo Server
  await server.start();

  // 应用 GraphQL 中间件
  app.use(
    '/graphql',
    express.json(),
    expressMiddleware(server, {
      context: async ({ req }) => {
        // 可以在这里添加认证逻辑
        return {
          // 添加上下文数据
          req,
        };
      },
    })
  );

  logger.info('GraphQL server setup complete');
  logger.info('GraphQL endpoint: /graphql');
  logger.info('GraphQL subscriptions: ws://localhost:<port>/graphql');

  return { httpServer, apolloServer: server };
}

// GraphQL 查询示例
export const EXAMPLE_QUERIES = {
  // 获取所有任务
  GET_TASKS: `
    query GetTasks($status: TaskStatus, $limit: Int) {
      tasks(status: $status, limit: $limit) {
        id
        content
        status
        priority
        tags
        agentAssignments {
          primary {
            id
            name
          }
        }
        createdAt
        updatedAt
      }
    }
  `,

  // 获取项目统计
  GET_PROJECT_STATS: `
    query GetProjectStats {
      projectStats {
        totalTasks
        tasksByStatus {
          todo
          inProgress
          done
        }
        tasksByPriority {
          urgent
          high
          medium
          low
        }
        aiExecutedTasks
        aiSuccessRate
      }
    }
  `,

  // 搜索
  SEARCH: `
    query Search($query: String!) {
      search(query: $query) {
        tasks {
          id
          content
        }
        workflows {
          id
          name
        }
        totalCount
      }
    }
  `,
};

// GraphQL 变更示例
export const EXAMPLE_MUTATIONS = {
  // 创建任务
  CREATE_TASK: `
    mutation CreateTask($input: CreateTaskInput!) {
      createTask(input: $input) {
        id
        content
        status
        priority
      }
    }
  `,

  // 更新任务
  UPDATE_TASK: `
    mutation UpdateTask($input: UpdateTaskInput!) {
      updateTask(input: $input) {
        id
        content
        status
        priority
        updatedAt
      }
    }
  `,

  // 执行工作流
  EXECUTE_WORKFLOW: `
    mutation ExecuteWorkflow($input: ExecuteWorkflowInput!) {
      executeWorkflow(input: $input) {
        id
        status
        startTime
      }
    }
  `,
};

// GraphQL 订阅示例
export const EXAMPLE_SUBSCRIPTIONS = {
  // 任务更新订阅
  TASK_UPDATED: `
    subscription OnTaskUpdated($taskId: ID) {
      taskUpdated(taskId: $taskId) {
        id
        content
        status
        updatedAt
      }
    }
  `,

  // 工作流执行更新
  WORKFLOW_EXECUTION_UPDATE: `
    subscription OnWorkflowExecutionUpdate($executionId: ID!) {
      workflowExecutionUpdate(executionId: $executionId) {
        executionId
        status
        currentNode
        progress
        error
      }
    }
  `,

  // 系统事件
  SYSTEM_EVENT: `
    subscription OnSystemEvent {
      systemEvent {
        type
        timestamp
        data
      }
    }
  `,
};